// src/pages/earningsImport/RideHailingUploadHistory.js
import React, { useMemo, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../firebase/firebase";

const WEEKS = ["week1", "week2", "week3", "week4", "week5"];
const PLATFORMS = ["bolt", "uber", "careem", "yango", "other"];
const COMPANY_OPTIONS = [
  "Lolia Limousine for Luxury Motor Vehicles Services LLC",
  "Comfort Line Luxury Motor Vehicles Services LLC",
];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function toDate(value) {
  try {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value?._seconds === "number") return new Date(value._seconds * 1000);
    if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
    return new Date(value);
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const d = toDate(value);
  if (!d || Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function money(value) {
  const n = Number(value || 0);
  return n.toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function norm(value) {
  return String(value || "").trim().toLowerCase();
}

function sameText(a, b) {
  return norm(a) === norm(b);
}

function makeBatchTarget(row, fallback = {}) {
  return {
    uploadBatchId: String(row?.uploadBatchId || fallback.uploadBatchId || "").trim(),
    month: String(row?.month || fallback.month || "").trim(),
    week: String(row?.week || fallback.week || "").trim(),
    platform: String(row?.platform || fallback.platform || "").trim().toLowerCase(),
    company: String(row?.company || fallback.company || "").trim(),
    totalRows: Number(row?.totalRows || fallback.expectedRows || 0),
    historyDocId: row?.id || fallback.historyDocId || "",
    historyPath: row?.historyPath || "",
  };
}

function isExactMatch(data, target) {
  return (
    String(data?.uploadBatchId || "").trim() === target.uploadBatchId &&
    String(data?.month || "").trim() === target.month &&
    String(data?.week || "").trim() === target.week &&
    sameText(data?.platform, target.platform) &&
    sameText(data?.company, target.company)
  );
}

function Button({ children, tone = "primary", disabled, ...props }) {
  const colors = {
    primary: "#2563eb",
    danger: "#dc2626",
    neutral: "#475569",
    success: "#16a34a",
    warning: "#d97706",
  };

  return (
    <button
      {...props}
      disabled={disabled}
      style={{
        background: disabled ? "#94a3b8" : colors[tone] || colors.primary,
        color: "white",
        border: "none",
        borderRadius: 8,
        padding: "9px 14px",
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        marginRight: 8,
        ...props.style,
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, fontWeight: 700 }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "9px 10px",
  minHeight: 38,
};

export default function RideHailingUploadHistory() {
  const [month, setMonth] = useState(currentMonth());
  const [week, setWeek] = useState("week1");
  const [historyRows, setHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const [manual, setManual] = useState({
    uploadBatchId: "",
    platform: "bolt",
    company: COMPANY_OPTIONS[0],
    expectedRows: "",
  });

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");

  const previewTotal = useMemo(() => {
    if (!preview?.matches?.length) return { bank: 0, cash: 0, total: 0 };
    return preview.matches.reduce(
      (sum, item) => {
        const d = item.data || {};
        const bank = Number(d.bankIncome || 0);
        const cash = Number(d.cashIncome || 0);
        sum.bank += bank;
        sum.cash += cash;
        sum.total += bank + cash;
        return sum;
      },
      { bank: 0, cash: 0, total: 0 }
    );
  }, [preview]);

  async function loadHistory() {
    setHistoryError("");
    setMessage("");
    setPreview(null);
    setLoadingHistory(true);

    try {
      const ref = collection(db, "weeklyEarningsUploads", month, week);
      const snap = await getDocs(ref);
      const rows = snap.docs
        .map((docSnap) => ({
          id: docSnap.id,
          historyPath: docSnap.ref.path,
          historyRef: docSnap.ref,
          ...docSnap.data(),
        }))
        .sort((a, b) => {
          const da = toDate(a.createdAt)?.getTime() || 0;
          const dbb = toDate(b.createdAt)?.getTime() || 0;
          return dbb - da;
        });
      setHistoryRows(rows);
      if (!rows.length) {
        setMessage(`No upload history found for ${month} ${week}. You can still use Manual Batch Search.`);
      }
    } catch (error) {
      console.error("Failed to load upload history", error);
      setHistoryError(error.message || "Failed to load upload history");
    } finally {
      setLoadingHistory(false);
    }
  }

  async function findDriverUploadRows(target) {
    if (!target.uploadBatchId || !target.month || !target.week || !target.platform || !target.company) {
      throw new Error("Batch ID, month, week, platform, and company are required.");
    }

    const driversSnap = await getDocs(collection(db, "driverWeeklyEarnings"));
    const matches = [];
    const skipped = [];

    for (const driverDoc of driversSnap.docs) {
      const uploadsRef = collection(db, "driverWeeklyEarnings", driverDoc.id, "uploads");
      const queryValues = [target.uploadBatchId];
      const numericBatchId = Number(target.uploadBatchId);
      if (!Number.isNaN(numericBatchId) && String(numericBatchId) === target.uploadBatchId) {
        queryValues.push(numericBatchId);
      }

      const seenDocPaths = new Set();

      for (const batchValue of queryValues) {
        const uploadsQuery = query(uploadsRef, where("uploadBatchId", "==", batchValue));
        const uploadSnap = await getDocs(uploadsQuery);

        uploadSnap.forEach((uploadDoc) => {
          if (seenDocPaths.has(uploadDoc.ref.path)) return;
          seenDocPaths.add(uploadDoc.ref.path);

          const data = uploadDoc.data();
          if (isExactMatch(data, target)) {
            matches.push({
              id: uploadDoc.id,
              path: uploadDoc.ref.path,
              ref: uploadDoc.ref,
              driverParentId: driverDoc.id,
              data,
            });
          } else {
            skipped.push({
              path: uploadDoc.ref.path,
              data,
            });
          }
        });
      }
    }

    return { matches, skipped, checkedDrivers: driversSnap.size };
  }

  async function previewDelete(row, options = {}) {
    setPreviewLoading(true);
    setPreview(null);
    setDeleteText("");
    setMessage("");

    try {
      const target = makeBatchTarget(row, {
        month,
        week,
        ...options,
      });

      const result = await findDriverUploadRows(target);

      setPreview({
        target,
        historyRef: row?.historyRef || null,
        historyPath: row?.historyPath || "",
        ...result,
      });

      if (result.matches.length === 0) {
        setMessage("Preview finished: no matching driver upload rows found.");
      }
    } catch (error) {
      console.error("Preview failed", error);
      alert(`Preview failed: ${error.message || error}`);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function previewManual() {
    await previewDelete(null, {
      uploadBatchId: manual.uploadBatchId,
      platform: manual.platform,
      company: manual.company,
      expectedRows: manual.expectedRows,
    });
  }

  async function deletePreviewedBatch() {
    if (!preview?.target) return;

    const { target, matches, historyRef } = preview;

    if (deleteText.trim() !== target.uploadBatchId) {
      alert("Please type the exact Batch ID before deleting.");
      return;
    }

    if (!matches.length) {
      alert("No matching rows to delete.");
      return;
    }

    const expectedRows = Number(target.totalRows || manual.expectedRows || 0);
    if (expectedRows > 0 && matches.length !== expectedRows) {
      const ok = window.confirm(
        `History/expected rows = ${expectedRows}, but preview found ${matches.length}.\n\nDelete only the ${matches.length} matched rows?`
      );
      if (!ok) return;
    } else {
      const ok = window.confirm(
        `This will delete ${matches.length} driver income rows for batch ${target.uploadBatchId}.\n\nContinue?`
      );
      if (!ok) return;
    }

    setDeleting(true);
    setMessage("");

    try {
      let batch = writeBatch(db);
      let batchCount = 0;
      let deletedRows = 0;

      for (const item of matches) {
        batch.delete(item.ref);
        batchCount += 1;
        deletedRows += 1;

        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (historyRef) {
        batch.delete(historyRef);
        batchCount += 1;
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      await addDoc(collection(db, "deletedWeeklyEarningsUploads"), {
        ...target,
        deletedRows,
        deletedHistoryPath: historyRef ? historyRef.path : "",
        deletedAt: new Date(),
        deletedBy: auth?.currentUser?.email || "unknown",
        source: "RideHailingUploadHistory",
        matchedPaths: matches.map((m) => m.path),
      });

      setMessage(`Deleted successfully: ${deletedRows} driver income rows${historyRef ? " + 1 upload history document" : ""}.`);
      setPreview(null);
      setDeleteText("");
      await loadHistory();
    } catch (error) {
      console.error("Delete failed", error);
      alert(`Delete failed: ${error.message || error}`);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 1350, margin: "0 auto", fontFamily: "Arial, sans-serif" }}>
      <div style={{ marginBottom: 22 }}>
        <h2 style={{ margin: 0 }}>Ride-Hailing Upload History</h2>
        <p style={{ margin: "8px 0 0", color: "#475569" }}>
          Preview and delete a wrong weekly ride-hailing upload batch without changing the existing upload logic.
        </p>
      </div>

      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, marginBottom: 18 }}>
        <h3 style={{ marginTop: 0 }}>1) Load upload history</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(160px, 1fr))", gap: 14, alignItems: "end" }}>
          <Field label="Month">
            <input value={month} onChange={(e) => setMonth(e.target.value)} placeholder="2026-07" style={inputStyle} />
          </Field>
          <Field label="Week">
            <select value={week} onChange={(e) => setWeek(e.target.value)} style={inputStyle}>
              {WEEKS.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          </Field>
          <div>
            <Button onClick={loadHistory} disabled={loadingHistory}>
              {loadingHistory ? "Loading..." : "Load History"}
            </Button>
          </div>
          <div style={{ color: "#64748b", fontSize: 13 }}>
            Reads: weeklyEarningsUploads / {month || "month"} / {week || "week"}
          </div>
        </div>
        {historyError && <div style={{ marginTop: 12, color: "#dc2626", fontWeight: 700 }}>{historyError}</div>}
      </div>

      {message && (
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e3a8a", borderRadius: 10, padding: 12, marginBottom: 18 }}>
          {message}
        </div>
      )}

      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, marginBottom: 18 }}>
        <h3 style={{ marginTop: 0 }}>Upload history records</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={th}>Created At</th>
                <th style={th}>Month</th>
                <th style={th}>Week</th>
                <th style={th}>Platform</th>
                <th style={th}>Company</th>
                <th style={th}>Batch ID</th>
                <th style={th}>Rows</th>
                <th style={th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.map((row) => (
                <tr key={row.id}>
                  <td style={td}>{formatDateTime(row.createdAt)}</td>
                  <td style={td}>{row.month}</td>
                  <td style={td}>{row.week}</td>
                  <td style={td}>{row.platform}</td>
                  <td style={td}>{row.company}</td>
                  <td style={{ ...td, fontFamily: "monospace" }}>{row.uploadBatchId}</td>
                  <td style={td}>{row.totalRows || "-"}</td>
                  <td style={td}>
                    <Button tone="warning" onClick={() => previewDelete(row)} disabled={previewLoading}>
                      Preview Delete
                    </Button>
                  </td>
                </tr>
              ))}
              {!historyRows.length && (
                <tr>
                  <td colSpan={8} style={{ ...td, textAlign: "center", color: "#64748b" }}>
                    No history loaded yet, or no records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, padding: 18, marginBottom: 18 }}>
        <h3 style={{ marginTop: 0 }}>Manual Batch Search</h3>
        <p style={{ color: "#7c2d12", marginTop: 0 }}>
          Use this if the upload history document was already deleted but driver income rows still exist.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(150px, 1fr))", gap: 14, alignItems: "end" }}>
          <Field label="Batch ID">
            <input
              value={manual.uploadBatchId}
              onChange={(e) => setManual((p) => ({ ...p, uploadBatchId: e.target.value }))}
              placeholder="1782990707145"
              style={inputStyle}
            />
          </Field>
          <Field label="Platform">
            <select value={manual.platform} onChange={(e) => setManual((p) => ({ ...p, platform: e.target.value }))} style={inputStyle}>
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Company">
            <input
              list="company-options"
              value={manual.company}
              onChange={(e) => setManual((p) => ({ ...p, company: e.target.value }))}
              style={inputStyle}
            />
            <datalist id="company-options">
              {COMPANY_OPTIONS.map((c) => <option key={c} value={c} />)}
            </datalist>
          </Field>
          <Field label="Expected rows optional">
            <input
              value={manual.expectedRows}
              onChange={(e) => setManual((p) => ({ ...p, expectedRows: e.target.value }))}
              placeholder="6"
              style={inputStyle}
            />
          </Field>
          <div>
            <Button tone="warning" onClick={previewManual} disabled={previewLoading || !manual.uploadBatchId}>
              {previewLoading ? "Checking..." : "Preview Manual Batch"}
            </Button>
          </div>
        </div>
      </div>

      {preview && (
        <div style={{ background: "#ffffff", border: "2px solid #2563eb", borderRadius: 12, padding: 18, marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>Delete Preview</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
            <Info label="Batch ID" value={preview.target.uploadBatchId} mono />
            <Info label="Month / Week" value={`${preview.target.month} / ${preview.target.week}`} />
            <Info label="Platform" value={preview.target.platform} />
            <Info label="Expected rows" value={preview.target.totalRows || manual.expectedRows || "-"} />
            <Info label="Matched driver rows" value={preview.matches.length} strong />
            <Info label="Checked drivers" value={preview.checkedDrivers} />
            <Info label="History doc" value={preview.historyPath || "No history doc selected"} />
            <Info label="Company" value={preview.target.company} />
          </div>

          <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, marginBottom: 14 }}>
            <strong>Matched totals:</strong> Bank AED {money(previewTotal.bank)} | Cash AED {money(previewTotal.cash)} | Total AED {money(previewTotal.total)}
          </div>

          <div style={{ overflowX: "auto", maxHeight: 300, border: "1px solid #e2e8f0", borderRadius: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={th}>Driver</th>
                  <th style={th}>Platform</th>
                  <th style={th}>Bank</th>
                  <th style={th}>Cash</th>
                  <th style={th}>Path</th>
                </tr>
              </thead>
              <tbody>
                {preview.matches.map((item) => (
                  <tr key={item.path}>
                    <td style={td}>{item.data.driverName || item.data.driverId || "-"}</td>
                    <td style={td}>{item.data.platform}</td>
                    <td style={td}>{money(item.data.bankIncome)}</td>
                    <td style={td}>{money(item.data.cashIncome)}</td>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>{item.path}</td>
                  </tr>
                ))}
                {!preview.matches.length && (
                  <tr><td style={td} colSpan={5}>No matching rows found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16, padding: 14, borderRadius: 10, background: "#fef2f2", border: "1px solid #fecaca" }}>
            <div style={{ fontWeight: 800, color: "#991b1b", marginBottom: 8 }}>
              Safety confirmation required
            </div>
            <p style={{ marginTop: 0 }}>
              To delete, type the exact Batch ID below. This deletes only the matched driver upload rows and the selected upload history document if available.
            </p>
            <input
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder={preview.target.uploadBatchId}
              style={{ ...inputStyle, minWidth: 280, marginRight: 10 }}
            />
            <Button
              tone="danger"
              onClick={deletePreviewedBatch}
              disabled={deleting || deleteText.trim() !== preview.target.uploadBatchId || !preview.matches.length}
            >
              {deleting ? "Deleting..." : "Delete This Batch"}
            </Button>
            <Button tone="neutral" onClick={() => setPreview(null)} disabled={deleting}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, mono, strong }) {
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 10 }}>
      <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 5, fontFamily: mono ? "monospace" : "inherit", fontWeight: strong ? 900 : 600 }}>
        {value || "-"}
      </div>
    </div>
  );
}

const th = {
  textAlign: "left",
  borderBottom: "1px solid #e2e8f0",
  padding: "10px 8px",
  color: "#334155",
  whiteSpace: "nowrap",
};

const td = {
  borderBottom: "1px solid #e2e8f0",
  padding: "9px 8px",
  verticalAlign: "top",
};
